import React, { FC, JSX, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'src/utils/axios';
import OtpCodeInput from 'src/components/shared/OtpCodeInput';
import OtpResendBlock from 'src/components/shared/OtpResendBlock';
import { useOtpResend } from 'src/hooks/useOtpResend';
import ulcmContactService from 'src/services/UlcmContact.service';
import { setHasPendingVerifications } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';

// kind='otp'     — in-flight UserOTP awaiting confirmation (has otp_id already)
// kind='contact' — unverified contact with no live OTP; propose issues one on demand
interface PendingOtpItem {
  kind: 'otp';
  otp_id: string;
  purpose: string;
  contact_type: string;
  masked: string;
  expires_at: string;
}

interface PendingContactItem {
  kind: 'contact';
  contact_type: string;
  contact_value: string;
  masked: string;
}

type PendingItem = PendingOtpItem | PendingContactItem;

// After propose/issue, we enter the OTP entry step with these fields.
interface ActiveOtp {
  otp_id: string;
  purpose: string; // sent to confirmOtp — must match backend purpose
  label: string; // shown in the dialog title
  masked: string;
}

const PURPOSE_LABELS: Record<string, string> = {
  contact_change: 'Contact verification',
  self_reg_verify: 'Account verification',
  account_claim: 'Account reactivation',
  rent_roll_initial_verify: 'Initial verification',
};

const getOtpLabel = (purpose: string, contactType: string): string => {
  if (purpose !== 'contact_change') {
    return PURPOSE_LABELS[purpose] || purpose;
  }
  if (contactType === 'phone') {
    return 'Phone Number';
  }
  return 'Email Address';
};

interface PendingVerificationsModalProps {
  onDismiss?: () => void;
  onAllVerified?: () => void;
}

const PendingVerificationsModal: FC<PendingVerificationsModalProps> = ({
  onDismiss,
  onAllVerified,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Per-contact proposing loading state (for kind='contact' rows)
  const [proposingContact, setProposingContact] = useState<string | null>(null);

  const [activeOtp, setActiveOtp] = useState<ActiveOtp | null>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null
  );
  const [isLocked, setIsLocked] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchPending = useCallback(async (): Promise<void> => {
    try {
      const resp = await axios.get('v2/user/ulcm/pending-verifications/');
      const items: PendingItem[] = resp.data?.pending || [];
      dispatch(setHasPendingVerifications(items.length > 0));
      if (items.length > 0) {
        setPending(items);
        setOpen(true);
      }
    } catch {
      dispatch(setHasPendingVerifications(false));
      // Silently fail — non-critical UX enhancement
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleDismiss = (): void => {
    setOpen(false);
    onDismiss?.();
  };

  const enterOtpStep = (otp: ActiveOtp): void => {
    setActiveOtp(otp);
    setCode('');
    setError('');
    setSuccessMsg('');
    setAttemptsRemaining(null);
    setIsLocked(false);
  };

  // kind='otp': code already sent, jump straight to entry
  const handleStartVerifyOtp = (item: PendingOtpItem): void => {
    enterOtpStep({
      otp_id: item.otp_id,
      purpose: item.purpose,
      label: getOtpLabel(item.purpose, item.contact_type),
      masked: item.masked,
    });
  };

  // kind='contact': call propose to issue a fresh OTP, then enter OTP step
  const handleStartVerifyContact = async (
    item: PendingContactItem
  ): Promise<void> => {
    setProposingContact(item.contact_value);
    setError('');
    try {
      let result;
      if (item.contact_type === 'phone') {
        result = await ulcmContactService.proposePhone(item.contact_value);
      } else {
        result = await ulcmContactService.proposeEmail(item.contact_value);
      }
      enterOtpStep({
        otp_id: result.otp_id,
        purpose: 'contact_change',
        label: item.contact_type === 'phone' ? 'Phone number' : 'Email address',
        masked: item.masked,
      });
    } catch {
      setError('Could not send verification code. Please try again.');
    } finally {
      setProposingContact(null);
    }
  };

  const handleBackToList = (): void => {
    setActiveOtp(null);
    setCode('');
    setError('');
    setSuccessMsg('');
    setAttemptsRemaining(null);
    setIsLocked(false);
  };

  const resendSendFn = useCallback(async (): Promise<unknown> => {
    if (!activeOtp) {
      throw new Error('No active OTP');
    }
    const resp = await axios.post(
      `v2/user/ulcm/pending-verifications/${activeOtp.otp_id}/resend/`
    );
    setCode('');
    if (resp.data?.otp_id) {
      setActiveOtp({ ...activeOtp, otp_id: resp.data.otp_id });
    }
    return resp.data;
  }, [activeOtp]);

  const resend = useOtpResend({
    sendFn: resendSendFn,
    defaultCooldownSeconds: 30,
  });

  const handleSubmitCode = async (): Promise<void> => {
    if (code.length !== 6 || !activeOtp) {
      return;
    }

    setSubmitting(true);
    setError('');
    setAttemptsRemaining(null);
    try {
      const result = await ulcmContactService.confirmOtp(
        activeOtp.purpose,
        code
      );
      if (result.confirmed) {
        setSuccessMsg('Contact verified.');
        let nextPending: PendingItem[] = [];
        setPending((prev) => {
          nextPending = prev
            .filter((p) => {
              if (p.kind === 'otp') {
                return p.otp_id !== activeOtp.otp_id;
              }
              return true;
            })
            .filter((p) => {
              if (p.kind === 'contact') {
                return p.masked !== activeOtp.masked;
              }
              return true;
            });
          return nextPending;
        });
        setTimeout(() => {
          setActiveOtp(null);
          setSuccessMsg('');
          if (nextPending.length === 0) {
            dispatch(setHasPendingVerifications(false));
            setOpen(false);
            onAllVerified?.();
          }
        }, 1500);
      } else if (result.is_locked) {
        setIsLocked(true);
      } else {
        setAttemptsRemaining(result.attempts_remaining);
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !open || pending.length === 0) {
    return null;
  }

  const renderVerifyStep = (): JSX.Element => {
    if (!activeOtp) {
      return <></>;
    }

    if (isLocked) {
      return (
        <Alert severity="error">
          Too many attempts. This contact has been locked. Please contact
          compliance to reactivate.
        </Alert>
      );
    }

    if (successMsg) {
      return <Alert severity="success">{successMsg}</Alert>;
    }

    return (
      <Stack spacing={2}>
        <Typography variant="body1">
          A verification code was sent to <strong>{activeOtp.masked}</strong>.
          Enter it below to confirm.
        </Typography>

        <OtpCodeInput
          value={code}
          onChange={(val: string) => {
            setCode(val);
            setError('');
            setAttemptsRemaining(null);
          }}
        />

        {attemptsRemaining !== null && (
          <Alert severity="warning">
            {`Incorrect code. ${attemptsRemaining} attempt(s) remaining.`}
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ textAlign: 'center' }}>
          <OtpResendBlock resend={resend} />
        </Box>
      </Stack>
    );
  };

  const renderListItem = (item: PendingItem): JSX.Element => {
    if (item.kind === 'otp') {
      return (
        <ListItem
          key={item.otp_id}
          divider
          sx={{ py: 1.5 }}
          secondaryAction={
            <Button
              variant="contained"
              size="small"
              onClick={() => handleStartVerifyOtp(item)}
            >
              Verify
            </Button>
          }
        >
          <ListItemText
            primary={item.masked}
            slotProps={{ secondary: { component: 'div' } }}
            secondary={
              <Chip
                label={item.contact_type === 'phone' ? 'SMS' : 'Email'}
                size="small"
                variant="outlined"
              />
            }
          />
        </ListItem>
      );
    }

    // kind='contact': unverified contact, propose on click
    const isProposing = proposingContact === item.contact_value;
    return (
      <ListItem
        key={item.contact_value}
        divider
        sx={{ py: 1.5 }}
        secondaryAction={
          <Button
            variant="contained"
            size="small"
            disabled={isProposing}
            onClick={() => void handleStartVerifyContact(item)}
            startIcon={isProposing ? <CircularProgress size={14} /> : null}
          >
            Verify
          </Button>
        }
      >
        <ListItemText
          primary={item.masked}
          slotProps={{ secondary: { component: 'div' } }}
          secondary={
            <Chip
              label={item.contact_type === 'phone' ? 'SMS' : 'Email'}
              size="small"
              variant="outlined"
            />
          }
        />
      </ListItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        handleDismiss();
      }}
      maxWidth="sm"
      fullWidth
    >
      {activeOtp ? (
        <>
          <DialogTitle>Verify {activeOtp.label}</DialogTitle>
          <DialogContent>{renderVerifyStep()}</DialogContent>
          <DialogActions>
            <Button
              onClick={handleBackToList}
              color="inherit"
              disabled={submitting}
            >
              Back
            </Button>
            {!isLocked && !successMsg && (
              <Button
                variant="contained"
                onClick={handleSubmitCode}
                disabled={submitting || code.length < 6}
              >
                {submitting ? <CircularProgress size={20} /> : 'Confirm'}
              </Button>
            )}
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Verify Your Contact Information</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You have {pending.length} contact{pending.length > 1 ? 's' : ''}{' '}
              waiting to be verified. Verify{' '}
              {pending.length > 1 ? 'them' : 'it'} now to ensure you can receive
              important notifications.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <List disablePadding>
              {pending.map((item) => renderListItem(item))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDismiss} color="inherit">
              Remind me later
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PendingVerificationsModal;
